import React, { useState } from 'react'
import { Form, Button, Row, Col, Alert } from 'react-bootstrap'
import { ErrorMessage, Formik } from 'formik'
import { CardElement, useElements } from '@stripe/react-stripe-js'
import { useStripeWithHandling } from 'hooks/useStripeWithHandling'
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3'
import * as Yup from 'yup'
import { transactionProcessing, transactionFailed } from '../../../Pricing/actions'
import { createPaymentMethod } from '../../../services/payment'
import { switchPlan } from '../../../services/manage'
import { getUpgradeReason, isOnFreeTrial } from 'utils/general'
import { ERROR_CODE_MAP } from '../../../stripe.constants'
import * as Sentry from '@sentry/react'
import styles from '../UpgradePayModal.module.css'
// Removed StripeLoadError

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      border: 'solid 1px #333',
      color: '#32325d',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
}

const schema = Yup.object({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required')
})

const UpgradeFailedView = ({
  dispatch,
  email,
  selectedPlan,
  period,
  errorMsg,
  onRetryWithPreviousMethod,
  onUpgradeSuccess,
  googleReCaptchaProps,
  userProfile,
  handle3DSecure
}) => {
  const stripe = useStripeWithHandling()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)
  const [cardError, setCardError] = useState(null)
  const [isCardValid, setIsCardValid] = useState(false)

  const handleCardChange = (event) => {
    setIsCardValid(event.complete)
    if (event.error) {
      setCardError(event.error.message)
    } else {
      setCardError(null)
    }
  }

  const onSubmit = async (formData) => {
    if (!stripe || !elements) {
      setCardError('Payment system is still initializing. Please try again in a moment.')
      return
    }

    if (!isCardValid) {
      setCardError('Please complete your card information')
      return
    }

    setIsLoading(true)
    dispatch(transactionProcessing())

    try {
      const recaptchaValue =
        googleReCaptchaProps && googleReCaptchaProps.executeRecaptcha
          ? await googleReCaptchaProps.executeRecaptcha('payment')
          : ''
      const fullName = `${formData.firstName} ${formData.lastName}`.trim()

      const billingDetails = {
        email: email,
        name: fullName
      }

      const { error, paymentMethod } = await createPaymentMethod(
        stripe,
        elements.getElement(CardElement),
        billingDetails
      )

      if (error) {
        dispatch(transactionFailed({ errorCode: `CPM_FAILED - ${error.type}` }))
        Sentry.captureException(error, {
          tags: {
            section: 'upgrade-retry-payment-error'
          }
        })
        setIsLoading(false)
        return
      }

      const upgradeReason = getUpgradeReason()
      const isFreeTrial = isOnFreeTrial(userProfile)

      const upgradeData = {
        email,
        plan: selectedPlan.label,
        priceId: selectedPlan?.prices?.[period]?.stripe?.priceId,
        productId: selectedPlan?.id,
        upgradeReason,
        paymentMethodId: paymentMethod.id,
        headers: {
          'x-recaptcha': recaptchaValue
        },
        ...(isFreeTrial && { endTrial: true })
      }

      const upgradeResponse = await switchPlan(upgradeData)

      if (upgradeResponse.data?.requires_action) {
        await handle3DSecure(upgradeResponse.data, () => {
          setIsLoading(false)
          onUpgradeSuccess()
        })
      } else {
        setIsLoading(false)
        onUpgradeSuccess()
      }
    } catch (error) {
      const errorResponse = error?.response?.data || error

      const declineCode = ERROR_CODE_MAP[errorResponse?.declineCode]

      dispatch(
        transactionFailed({
          errorCode: errorResponse?.errorCode || 'UPGRADE_RETRY_FAILED',
          declineCode: errorResponse?.declineCode
        })
      )

      if (!declineCode) {
        Sentry.captureException(error, {
          tags: {
            section: 'upgrade-retry-error'
          }
        })
      }

      setIsLoading(false)
      onRetryWithPreviousMethod()
    }
  }

  return (
    <div className="p-4">
      <div className="text-center mb-4">
        <h4 className="color-purple mb-3">Upgrade Failed</h4>
        <p className="text-muted">Please try again or use a different payment method</p>
      </div>

      <Formik
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={{
          firstName: '',
          lastName: ''
        }}
      >
        {({ handleSubmit, handleChange, values }) => (
          <form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                <Form.Control
                  name="firstName"
                  type="text"
                  placeholder="First name"
                  value={values.firstName}
                  onChange={handleChange}
                />
                <ErrorMessage name="firstName" component="div" className="validation-error" />
              </Col>
              <Col>
                <Form.Control
                  name="lastName"
                  type="text"
                  placeholder="Last name"
                  value={values.lastName}
                  onChange={handleChange}
                />
                <ErrorMessage name="lastName" component="div" className="validation-error" />
              </Col>
            </Row>

            <div className="mb-3">
              <CardElement
                className={styles.StripeElement}
                options={CARD_ELEMENT_OPTIONS}
                onChange={handleCardChange}
              />
            </div>

            {cardError && (
              <Alert variant="danger" className="mt-2">
                {cardError}
              </Alert>
            )}

            <div className="p-1 mb-3" style={{ minHeight: 30 }}>
              <div className="opacity-50 font-14">
                Secured with{' '}
                <a href="https://stripe.com" target="_blank" rel="noreferrer">
                  <img src="/assets/icons/stripe.svg" height="30" alt="Stripe" />
                </a>
              </div>
            </div>

            <div className="d-grid gap-2">
              <Button type="submit" disabled={!stripe || !isCardValid || isLoading} className="mb-2">
                {isLoading ? 'Processing...' : 'Pay with new payment method'}
              </Button>

              <Button variant="outline-secondary" onClick={onRetryWithPreviousMethod} disabled={isLoading}>
                Try again with previous payment method
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  )
}

export default withGoogleReCaptcha(UpgradeFailedView)
